// One job, in full: what was sent, what the road did, and what came back.
//
// The console's other surfaces answer "which rounds need me?". This
// answers every question about one of them, in the order a dispatcher
// actually asks them: is it moving, what happened at each door, can I
// see the proof, and — last, because it is only needed once — what do I
// send to whom.
//
// Two things this screen must never do, both of which are easy and
// tempting:
//
// - **Infer.** An outcome is asserted (§5.2.1). A stop the van has
//   driven past is not delivered, and the timeline says nothing about it
//   rather than something plausible.
// - **Flatter.** A run nobody is publishing to is drawn as silence. The
//   whole product is worth nothing if this screen looks the same whether
//   the driver is working or their phone is off.

import {
  STOP_OUTCOME,
  STOP_REASON,
  THREAD_STATE,
  stopProgress,
  type FollowHandle,
  type FollowSnapshot,
  type StopOutcomeCode
} from "@wayfind/core";
import { recipientUrl } from "@wayfind/core";
import { Copyable, Note, Panel, Qr, downloadFile } from "./Bits";
import { FleetMap, type MapStop, type MapVehicle } from "./FleetMap";
import { useDeliveryProofs } from "../ops/proof";
import { useRemainingRoad } from "../ops/leg";
import type { Dispatcher } from "../ops/dispatch";
import type { RouteEngine } from "@wayfind/core";
import type { FleetState, StoredJob } from "../ops/state";
import { clockWords, dateWords, plural, type Vocabulary } from "../ops/words";

/** Why a stop was not delivered, in the words a person would say. */
const REASON_WORDS: Record<number, string> = {
  [STOP_REASON.ABSENT]: "nobody home",
  [STOP_REASON.REFUSED]: "refused at the door",
  [STOP_REASON.INACCESSIBLE]: "could not get to the door",
  [STOP_REASON.PARCEL_MISSING]: "the parcel was not on the van",
  [STOP_REASON.OTHER]: "another reason"
};

function outcomeWords(outcome: StopOutcomeCode): { label: string; tone: string } {
  switch (outcome) {
    case STOP_OUTCOME.DELIVERED: return { label: "Delivered", tone: "settled" };
    case STOP_OUTCOME.SKIPPED: return { label: "Skipped", tone: "halt" };
    case STOP_OUTCOME.FAILED: return { label: "Not delivered", tone: "halt" };
    default: return { label: "Nothing said yet", tone: "quiet" };
  }
}

export function JobDetail({
  job,
  snapshot,
  proof,
  state,
  dispatcher,
  engine,
  vocabulary,
  onBack,
  onHandOver,
  onFinish
}: {
  job: StoredJob;
  snapshot: FollowSnapshot | null;
  proof: FollowHandle["proof"] | null;
  state: FleetState;
  dispatcher: Dispatcher | null;
  engine: RouteEngine | null;
  vocabulary: Vocabulary;
  onBack(): void;
  onHandOver(job: StoredJob): void;
  onFinish(job: StoredJob): void;
}): JSX.Element {
  const status = snapshot?.status ?? null;
  const heard = status?.observed.ever ?? false;
  const progress = status ? stopProgress(status, snapshot?.plan ?? null) : null;
  const ended = status
    ? status.state === THREAD_STATE.COMPLETED || status.state === THREAD_STATE.CANCELED
    : job.finishedUnixSeconds != null;
  const routeDay = job.serviceDay != null;

  const proofs = useDeliveryProofs({
    job,
    proof,
    dispatcher,
    consolePrivateKeyHex: state.consoleDevice.privateKeyHex,
    chainHead: status?.photoChain ?? null
  });
  const photoFor = (stopIndex: number): string | null =>
    proofs.photos.filter(photo => photo.stopIndex === stopIndex).slice(-1)[0]?.url ?? null;

  const stops: MapStop[] = job.stops.map((stop, index) => {
    const outcome = (status?.outcomes[index] ?? STOP_OUTCOME.PENDING) as StopOutcomeCode;
    return {
      id: stop.id,
      lat: stop.lat,
      lon: stop.lon,
      label: stop.label,
      tone: outcome === STOP_OUTCOME.DELIVERED
        ? "delivered"
        : outcome === STOP_OUTCOME.PENDING ? "pending" : "missed",
      focused: status?.stopIndex === index + 1 && !ended
    };
  });
  // Drawn only on a live claim. Everything else — coarse, stale,
  // unplaceable — leaves the map without a van, which is the honest
  // picture and the one §12 authorises.
  // The doors this round has not settled yet, in plan order — what the
  // van still has to drive, and the input the drawn road is built from.
  const doors = job.stops
    .filter((_, index) => (status?.outcomes[index] ?? STOP_OUTCOME.PENDING) === STOP_OUTCOME.PENDING)
    .map(stop => ({ lat: stop.lat, lon: stop.lon }));
  const road = useRemainingRoad(engine, snapshot?.position ?? null, doors);

  const vehicles: MapVehicle[] = snapshot?.position
    ? [{
      id: job.id,
      lat: snapshot.position.lat,
      lon: snapshot.position.lon,
      label: job.driverName ?? state.devices.find(d => d.id === job.deviceId)?.name ?? "The vehicle",
      focused: true,
      headingDegrees: road.headingDegrees
    }]
    : [];

  return (
    <div className="stack">
      <div className="detailhead">
        <button type="button" className="button button--small" onClick={onBack}>← Back</button>
        <div className="detailhead__title">
          <h2 className="title">
            {job.stops.length === 1
              ? job.stops[0]!.label
              : `${plural(job.stops.length, vocabulary.stop)}, in order`}
          </h2>
          <p className="small muted">
            {dateWords(job.createdUnixSeconds)} · sealed {clockWords(job.createdUnixSeconds)} ·
            good until {clockWords(job.notAfter)}
            {routeDay ? ` · service day ${job.serviceDay}` : ""}
          </p>
        </div>
        <div className="row row--tight">
          {!ended ? (
            <>
              <button type="button" className="button button--small" onClick={() => onHandOver(job)}>
                Hand over
              </button>
              <button type="button" className="button button--small button--ghost" onClick={() => onFinish(job)}>
                Close it
              </button>
            </>
          ) : null}
        </div>
      </div>

      <LiveStrip
        job={job}
        state={state}
        snapshot={snapshot}
        heard={heard}
        ended={ended}
        progress={progress}
        vocabulary={vocabulary}
      />

      <div className="detailgrid">
        <div className="stack">
          <Panel eyebrow="On the ground" className="panel--flush">
            <FleetMap
              engine={engine}
              vehicles={vehicles}
              stops={stops}
              legs={road.track.length
                ? [{ id: job.id, track: road.track, range: road.range, selected: true }]
                : []}
            />
          </Panel>

          <Panel eyebrow={`What happened at each ${vocabulary.stop}`}>
            <ol className="timeline">
              {job.stops.map((stop, index) => {
                const outcome = (status?.outcomes[index] ?? STOP_OUTCOME.PENDING) as StopOutcomeCode;
                const words = outcomeWords(outcome);
                const reason = status?.lastOutcome?.stopIndex === index + 1
                  ? status.lastOutcome.reasonCode
                  : 0;
                const photo = photoFor(index + 1);
                // The run's `stopIndex` is the last stop it *dealt
                // with*, so after a mark it points at a door that is
                // already settled. "On the way" belongs only to a stop
                // nobody has spoken about yet.
                const current = status?.stopIndex === index + 1
                  && !ended
                  && outcome === STOP_OUTCOME.PENDING;
                return (
                  <li key={stop.id} className={`timeline__item timeline__item--${words.tone}`}>
                    <div className="timeline__mark" aria-hidden="true" />
                    <div className="timeline__body">
                      <p className="timeline__title">
                        <span className="timeline__index">{index + 1}</span>
                        {stop.label}
                        {current ? <span className="chip chip--now">on the way</span> : null}
                      </p>
                      <p className="timeline__state">
                        {words.label}
                        {outcome !== STOP_OUTCOME.PENDING && outcome !== STOP_OUTCOME.DELIVERED
                          && reason && REASON_WORDS[reason]
                          ? ` — ${REASON_WORDS[reason]}`
                          : ""}
                      </p>
                      {photo ? (
                        <figure className="proofshot">
                          <img src={photo} alt={`Taken at ${stop.label}`} />
                          <figcaption>
                            Sealed by the {vocabulary.driver} and opened here. The bytes match the
                            commitment on their signed record.
                          </figcaption>
                        </figure>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ol>

            <ProofFooter proofs={proofs} vocabulary={vocabulary} heard={heard} />
          </Panel>
        </div>

        <div className="stack">
          <Panel eyebrow={job.stops.length > 1
            ? `Give each ${vocabulary.recipient} their own`
            : `Give this to the ${vocabulary.recipient}`}>
            {job.stops.map((stop, index) => (
              <Copyable
                key={stop.id}
                label={job.stops.length > 1 ? `${index + 1}. ${stop.label}` : "Tracking link"}
                value={recipientUrl(job.trackUrl, { stopIndex: index + 1, lat: stop.lat, lon: stop.lon })}
                href={recipientUrl(job.trackUrl, { stopIndex: index + 1, lat: stop.lat, lon: stop.lon })}
                hint={index > 0 ? undefined : (
                  "78 bytes in the part of the address browsers never send anywhere. Whoever holds "
                  + "one can watch this run until it expires, and learns "
                  + (job.stops.length > 1
                    ? `nothing about any other ${vocabulary.stop} on it.`
                    : `nothing about any other ${vocabulary.stop} on it.`)
                )}
              />
            ))}
          </Panel>

          <Panel eyebrow={`Give this to the ${vocabulary.driver}`}>
            <div className="stack stack--tight">
              <Qr value={job.driverUrl} alt="The sealed job, as a code for a phone camera" />
              <Copyable label="The job, sealed" value={job.driverUrl} />
              <div className="row">
                <button
                  type="button"
                  className="button button--small"
                  onClick={() => downloadFile(
                    `${job.jobIdHex.slice(0, 8)}.wayfindjob`,
                    job.driverUrl,
                    "text/plain"
                  )}
                >
                  Download the file
                </button>
              </div>
              <Note title="It only opens on the phone it was sealed to." tone="quiet">
                What the camera reads is ciphertext. A photo of the code on a counter is worth
                nothing to anyone else in the room.
              </Note>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

/**
 * The one line that says whether this is happening.
 *
 * Deliberately the largest thing on the screen after the address: an
 * operator opening a job at 14:00 is asking one question, and every
 * other panel here is the follow-up.
 */
function LiveStrip({
  job,
  state,
  snapshot,
  heard,
  ended,
  progress,
  vocabulary
}: {
  job: StoredJob;
  state: FleetState;
  snapshot: FollowSnapshot | null;
  heard: boolean;
  ended: boolean;
  progress: { delivered: number; total: number; skipped: number; failed: number } | null;
  vocabulary: Vocabulary;
}): JSX.Element {
  const status = snapshot?.status ?? null;
  const who = state.devices.find(device => device.id === job.deviceId)?.name
    ?? job.driverName
    ?? (job.heldBy ? "This console" : "Nobody yet");

  const headline = !heard
    ? (ended ? "Closed without a word" : "Not started")
    : ended
      ? "Finished"
      : status?.position.kind === "live"
        ? "On the road"
        : status?.position.kind === "stale"
          ? "Gone quiet"
          : status?.position.kind === "withheld"
            ? `Publishing ${vocabulary.stop} events only`
            : "Publishing";

  const tone = !heard ? "quiet" : ended ? "settled" : status?.observed.stale ? "halt" : "live";

  return (
    <div className={`livestrip livestrip--${tone}`}>
      <div>
        <p className="livestrip__eyebrow">{who}</p>
        <p className="livestrip__headline">{headline}</p>
      </div>
      <div className="livestrip__facts">
        {progress ? (
          <span className="livestrip__fact">
            <b>{progress.delivered}</b> of {progress.total} delivered
          </span>
        ) : null}
        {progress?.skipped ? <span className="livestrip__fact livestrip__fact--halt">{progress.skipped} skipped</span> : null}
        {progress?.failed ? <span className="livestrip__fact livestrip__fact--halt">{progress.failed} not delivered</span> : null}
        {status?.observed.ageSeconds != null ? (
          <span className="livestrip__fact">
            heard {status.observed.ageSeconds < 60
              ? "just now"
              : `${Math.round(status.observed.ageSeconds / 60)} min ago`}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** What the proof collection did, when it has something to say about it. */
function ProofFooter({
  proofs,
  vocabulary,
  heard
}: {
  proofs: ReturnType<typeof useDeliveryProofs>;
  vocabulary: Vocabulary;
  heard: boolean;
}): JSX.Element | null {
  if (!heard) return null;
  if (proofs.collecting) {
    return (
      <p className="small muted" style={{ marginTop: "0.6rem" }}>
        <span className="spinner" aria-hidden="true" /> Fetching the proof off the mesh…
      </p>
    );
  }
  if (proofs.photos.length) {
    return (
      <p className="small muted" style={{ marginTop: "0.6rem" }}>
        {plural(proofs.photos.length, "photo")} opened with the run seed from this job’s own ticket.
        Whoever holds a tracking link can see that they exist and cannot open one.
        {proofs.missing ? ` ${proofs.missing} more is committed to and nobody reachable is holding it.` : ""}
      </p>
    );
  }
  if (proofs.problem) {
    return (
      <p className="small muted" style={{ marginTop: "0.6rem" }}>
        {proofs.problem}{" "}
        <button type="button" className="button button--small button--ghost" onClick={proofs.collect}>
          Try again
        </button>
      </p>
    );
  }
  return (
    <p className="small muted" style={{ marginTop: "0.6rem" }}>
      No photograph has been published on this {vocabulary.run}.
    </p>
  );
}
